import {
  ExternalLink,
  FolderOpen,
  Link,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, useConfirm, useToast } from "@/components/shared";
import { projectService } from "@/lib/services";
import type { ProjectSummary } from "@/types";

export function Projects() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await projectService.list({ pageSize: 100 });
      setProjects(response.data);
    } catch (err) {
      console.error("Failed to load projects:", err);
      showToast("Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleDeleteProject = async (projectUuid: string) => {
    setSelectedProject(null);
    const confirmed = await confirm({
      title: "Delete Project",
      message: "Are you sure you want to delete this project? This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await projectService.delete(projectUuid);
      showToast("Project deleted successfully", "success");
      await fetchProjects();
    } catch (err) {
      console.error("Failed to delete project:", err);
      showToast("Failed to delete project", "error");
    }
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreateProject = async () => {
    if (!formData.name.trim()) {
      showToast("Please enter a project name", "warning");
      return;
    }

    try {
      setCreating(true);
      const response = await projectService.create({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      });
      showToast("Project created successfully", "success");
      setShowCreateModal(false);
      setFormData({ name: "", description: "" });
      navigate(`/projects/${response.data.uuid}`);
    } catch (err) {
      console.error("Failed to create project:", err);
      showToast("Failed to create project", "error");
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string; label: string }> = {
      active: { bg: "bg-green-500/10", text: "text-green-400", label: "Active" },
      completed: { bg: "bg-blue-500/10", text: "text-blue-400", label: "Completed" },
      archived: { bg: "bg-gray-500/10", text: "text-gray-400", label: "Archived" },
    };
    const c = config[status] || config.active;
    return <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{c.label}</span>;
  };

  const getRoleBadge = (role?: string) => {
    if (!role) return null;
    const config: Record<string, { bg: string; text: string }> = {
      owner: { bg: "bg-amber-500/10", text: "text-amber-400" },
      editor: { bg: "bg-blue-500/10", text: "text-blue-400" },
      viewer: { bg: "bg-gray-500/10", text: "text-gray-400" },
    };
    const c = config[role] || config.viewer;
    return <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text} capitalize`}>{role}</span>;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={FolderOpen}
        title="Projects"
        subtitle="Organize your work with curated resources"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            New Project
          </button>
        }
      >
        {/* Search */}
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          />
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {filteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <div
                key={project.uuid}
                role="button" // NOSONAR — contains nested interactive elements
                tabIndex={0}
                onClick={() => navigate(`/projects/${project.uuid}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/projects/${project.uuid}`);
                  }
                }}
                className="bg-surface-primary border border-border-primary rounded-xl p-5 hover:border-border-secondary transition-colors group cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div
                      className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${project.color}20` }}
                    >
                      <FolderOpen className="h-6 w-6" style={{ color: project.color }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-content-primary truncate group-hover:text-brand-primary transition-colors">
                        {project.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(project.status)}
                        {getRoleBadge(project.currentUserRole)}
                      </div>
                    </div>
                  </div>
                  {(project.currentUserRole === "owner" || project.currentUserRole === "editor") && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProject(project.uuid);
                      }}
                      className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {project.description && (
                  <p className="mt-3 text-sm text-content-tertiary line-clamp-2">{project.description}</p>
                )}

                {/* Footer */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-sm text-content-tertiary">
                    <div className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      <span>{project.memberCount}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Link className="h-3.5 w-3.5" />
                      <span>{project.resourceCount}</span>
                    </div>
                  </div>
                  <span className="text-xs text-brand-primary font-medium">Open</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
              <FolderOpen className="h-8 w-8 text-brand-primary" />
            </div>
            <h3 className="text-lg font-medium text-content-primary">No projects yet</h3>
            <p className="text-content-tertiary mt-1 text-center max-w-sm">
              {searchQuery
                ? "Try adjusting your search query"
                : "Create a project to organize agents, data sources, conversations, and links in one place."}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <Plus className="h-4 w-4" />
                New Project
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">New Project</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ name: "", description: "" });
                }}
                disabled={creating}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="project-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Project Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="project-name"
                  type="text"
                  placeholder="e.g., Q1 Product Launch"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              <div>
                <label
                  htmlFor="project-description"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Description (optional)
                </label>
                <textarea
                  id="project-description"
                  rows={3}
                  placeholder="What is this project about?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ name: "", description: "" });
                }}
                disabled={creating}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={creating || !formData.name.trim()}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Creating...
                  </>
                ) : (
                  "Create Project"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions Dropdown */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-40"
          role="presentation" // NOSONAR — decorative backdrop element
          onClick={() => setSelectedProject(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setSelectedProject(null);
          }}
        >
          <div // NOSONAR - context menu container, stopPropagation prevents backdrop dismiss
            className="absolute right-10 top-40 bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 min-w-[160px]"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                navigate(`/projects/${selectedProject}`);
                setSelectedProject(null);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Open Project
            </button>
            <button
              onClick={() => {
                navigate(`/projects/${selectedProject}`);
                setSelectedProject(null);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <Pencil className="h-4 w-4" />
              Edit Project
            </button>
            <div className="border-t border-border-primary my-1" />
            <button
              onClick={() => handleDeleteProject(selectedProject)}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-interactive-hover transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Project
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
